import { POSIcon, POSLogo } from '@publicisgroupe/p-os-components';
import { NewsFeedTile } from 'components/NewsFeedTile/NewsFeedTile';
import { useEffect, useState } from 'react';
import { Container } from 'semantic-ui-react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { t } from 'utils/resources';

import { Dictionary, Notification, NotificationFeed, TimedNotificationFeed } from 'types/types';
import './GlobalNotification.less';

interface NotificationProps {
  handleNotificationSidebar: () => void;
  handleNotificationOnClose: (messageId: string) => void;
  messages: Notification[];
  handleNotificationClick: (data: Dictionary<string>) => void;
}

export const GlobalNotification = ({
  handleNotificationSidebar,
  handleNotificationOnClose,
  messages,
  handleNotificationClick,
}: NotificationProps) => {
  const [notificationFeed, setNotificationFeed] = useState<NotificationFeed>({
    hasNotifications: false,
    notifications: [],
  });

  useEffect(() => {
    const currentDate = new Date();

    const todayNotificationFeed: TimedNotificationFeed = {
      sectionHeader: t('NotificationSection.Today'),
      notifications: [],
    };
    const lastSevenDaysNotificationFeed: TimedNotificationFeed = {
      sectionHeader: t('NotificationSection.LastSevenDays'),
      notifications: [],
    };
    const allTimeNotificationFeed: TimedNotificationFeed = {
      sectionHeader: t('NotificationSection.AllTime'),
      notifications: [],
    };

    // Normally, this could be done by filtering since filter/forEach don't have such performance
    // differences when iterating over 1000000 of messages.
    // However, we call the "moment" method in here which affects performance, so we're using forEach
    // to only go through all notifications once
    messages.forEach((notification) => {
      const diffDays = moment(currentDate).diff(notification.createdAt, 'days');
      if (diffDays === 0) {
        todayNotificationFeed.notifications.push(notification);
      } else if (diffDays > 0 && diffDays < 7) {
        lastSevenDaysNotificationFeed.notifications.push(notification);
      } else {
        allTimeNotificationFeed.notifications.push(notification);
      }
    });

    const notificationsFeed: NotificationFeed = {
      hasNotifications: messages.length > 0,
      notifications: [
        todayNotificationFeed,
        lastSevenDaysNotificationFeed,
        allTimeNotificationFeed,
      ],
    };

    setNotificationFeed(notificationsFeed);
  }, [messages]);

  return (
    <>
      <Container className="notification-container">
        <POSIcon
          icon="close"
          className="large close-icon"
          onClick={() => handleNotificationSidebar && handleNotificationSidebar()}
        />
        <p className="notification-container-title">{t('NotificationSection.Title')}</p>
        {!notificationFeed.hasNotifications && (
          <div className="notification-container-no-data">
            <POSLogo color={'colored'} inverted />
            <p className="notification-container-no-data-text">{t('NotificationSection.NoData')}</p>
          </div>
        )}
        {notificationFeed.notifications.map(
          (feed) =>
            feed.notifications.length > 0 && (
              <section className="notification-container-section" key={`feed-${uuidv4()}`}>
                <p className="notification-container-section-header">{feed.sectionHeader}</p>
                <div className="notification-container-section-feeds">
                  {feed.notifications.map((data: Notification) => (
                    <NewsFeedTile
                      key={data.id}
                      data={data}
                      handleNotificationOnClose={() => handleNotificationOnClose(data.id)}
                      handleNotificationSidebar={() => handleNotificationSidebar()}
                      handleNotificationClick={(context: Dictionary<string>) =>
                        handleNotificationClick(context)
                      }
                    />
                  ))}
                </div>
              </section>
            )
        )}
      </Container>
    </>
  );
};
